import { useState } from 'react'
import { SEO } from '@/components/layout/SEO'
import { PageHeader, Btn, FormField } from '@/components/ui'
import { C } from '@/constants/theme'
import { WA_NUMBER } from '@/constants/data'
import type { ContactFormData } from '@/types'

const HOURS = [
  ['Monday – Friday', '7:30am – 5:00pm'],
  ['Saturday',        '8:00am – 1:00pm'],
  ['Sunday',          'Closed'],
]

const CONTACTS = [
  { icon: '📞', label: 'Phone',   val: '+27 69 214 5065',          href: 'tel:+27692145065' },
  { icon: '✉️', label: 'Email',   val: 'sales@glasstech.co.za',  href: 'mailto:sales@glasstech.co.za' },
  { icon: '📍', label: 'Address', val: '85 Flamingo Road, Kikuyu Estate, Midrand, 2090' },
]

const EMPTY: ContactFormData = { name: '', email: '', phone: '', message: '' }

export default function Contact() {
  const [form, setForm] = useState<ContactFormData>(EMPTY)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const set = (key: keyof ContactFormData) => (v: string) => setForm(f => ({ ...f, [key]: v }))

  const handleSubmit = () => {
    if (!form.name || !form.message) {
      setError('Please fill in your name and message.')
      return
    }
    setError('')
    const lines = [
      `Hi GlassTech! I have a message for you.`,
      ``,
      `Name: ${form.name}`,
      form.email ? `Email: ${form.email}` : null,
      form.phone ? `Phone: ${form.phone}` : null,
      ``,
      form.message,
    ].filter(l => l !== null).join('\n')
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines)}`, '_blank')
    setSent(true)
  }

  return (
    <>
      <SEO
        title="Contact Us"
        description="Get in touch with GlassTech Pty Ltd. Call +27 69 214 5065 or email us. Based in Midrand, Gauteng. Mobile auto glass service across Gauteng."
        path="/contact"
      />

      <PageHeader label="Reach Us" title="Contact Us" />

      <div style={{ background: C.grayLight, minHeight: '80vh' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px' }}>
          <div className="gt-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 32 }}>

            {/* Contact info */}
            <div>
              {CONTACTS.map(c => (
                <div key={c.label} style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                  <div style={{ width: 44, height: 44, background: C.blueLight, border: `1px solid rgba(0,174,239,0.2)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {c.icon}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 10, letterSpacing: 2, color: C.blue, textTransform: 'uppercase', marginBottom: 3 }}>{c.label}</div>
                    {c.href
                      ? <a href={c.href} style={{ color: C.text, fontSize: 15, textDecoration: 'none' }}>{c.val}</a>
                      : <div style={{ color: C.text, fontSize: 15 }}>{c.val}</div>
                    }
                  </div>
                </div>
              ))}

              {/* Hours */}
              <div style={{ background: C.white, border: `1px solid ${C.grayBorder}`, borderLeft: `3px solid ${C.blue}`, padding: 22, marginTop: 8 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 10, letterSpacing: 3, color: C.blue, textTransform: 'uppercase', marginBottom: 14 }}>Office Hours</div>
                {HOURS.map(([day, time]) => (
                  <div key={day} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.grayMid}` }}>
                    <span style={{ color: C.textMid, fontSize: 13 }}>{day}</span>
                    <span style={{ color: C.text, fontSize: 13, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>{time}</span>
                  </div>
                ))}
              </div>

              {/* Google Maps placeholder */}
              <div style={{ marginTop: 24, background: C.grayMid, border: `1px solid ${C.grayBorder}`, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 28 }}>📍</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: C.textMid, letterSpacing: 1 }}>
                  {/* TODO: replace with <iframe> Google Maps embed */}
                  Google Maps embed — coming soon
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div style={{ background: C.white, border: `1px solid ${C.grayBorder}`, borderTop: `3px solid ${C.blue}`, padding: 36, boxShadow: '0 2px 16px rgba(0,0,0,0.04)' }}>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 8 }}>WhatsApp is opening!</div>
                  <div style={{ color: C.textMid, lineHeight: 1.7 }}>Press <strong>Send</strong> in WhatsApp to deliver your message.</div>
                  <button onClick={() => { setSent(false); setForm(EMPTY) }} style={{ marginTop: 24, background: 'none', border: 'none', color: C.blue, cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", fontSize: 13, letterSpacing: 1 }}>
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 24 }}>Send a Message</div>
                  <FormField label="Full Name *"    value={form.name}    onChange={set('name')}    placeholder="John Smith" />
                  <FormField label="Email Address"  value={form.email}   onChange={set('email')}   type="email" placeholder="john@email.com" />
                  <FormField label="Phone"          value={form.phone}   onChange={set('phone')}   type="tel"   placeholder="+27 ..." />
                  <FormField label="Message *"      value={form.message} onChange={set('message')} rows={4} placeholder="How can we help you?" />
                  {error && (
                    <div style={{ marginBottom: 16, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontSize: 13 }}>
                      {error}
                    </div>
                  )}
                  <Btn onClick={handleSubmit}>Send via WhatsApp</Btn>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) { .gt-two-col { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  )
}
